import type React from 'react'
import type { ReactNode } from 'react'
import { Fragment, useState } from 'react'
import { Icon } from 'shared-ui/components'
import { createVariants } from 'shared-ui/lib'
import { isNonNil } from 'utils'

import { SpriteMap } from '../global'

interface SideBarItem {
  key: string
  title?: string
  component: ReactNode
}

interface SideBarProps {
  items: SideBarItem[]
  bottomAction?: ReactNode
}

const collapsibleDrawerClasses = createVariants({
  base: 'lg:hidden fixed top-0 h-screen w-[300px] sm:w-[400px] bg-white border-r border-gray-300 z-[95] transition-transform duration-300 ease-in-out flex flex-col',
  variants: {
    isOpen: {
      true: 'translate-x-0',
      false: '-translate-x-full'
    }
  },
  defaultVariants: {
    isOpen: false
  }
})

const collapsibleDrawerUnderlayClasses = createVariants({
  base: 'lg:hidden fixed inset-0 bg-black/50 z-[90]',
  variants: {
    isOpen: {
      true: 'block',
      false: 'hidden'
    }
  }
})

const hamburgerButtonClasses = createVariants({
  base: 'lg:hidden mt-2 fixed left-2 z-50 p-2 rounded-lg bg-white border border-gray-300 shadow-sm hover:bg-gray-50',
  variants: {
    isOpen: {
      true: 'hidden',
      false: 'block'
    }
  }
})

export const SideBar: React.FC<SideBarProps> = ({ items, bottomAction }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false)

  const sidebarContent = (
    <div className="h-full min-w-[300px] overflow-y-auto bg-white">
      <div className="flex flex-col gap-y-2 px-4 pt-4">
        {items.map((item) => (
          <Fragment key={item.key}>
            {isNonNil(item.title) && (
              <p className="pan-label-wide text-sm uppercase leading-5 tracking-[0.06em]">
                {item.title}
              </p>
            )}
            {item.component}
          </Fragment>
        ))}
      </div>

      {isNonNil(bottomAction) && (
        <div className="fixed bottom-4 left-4 right-4 flex max-w-[360px]">{bottomAction}</div>
      )}
    </div>
  )

  return (
    <div className="z-50 flex print:hidden">
      <button
        className={hamburgerButtonClasses({ isOpen: isMobileOpen })}
        onClick={() => setIsMobileOpen(true)}
      >
        <Icon icon={SpriteMap.HamburgerIcon} className="size-4 text-green-500" />
      </button>

      <div className="hidden w-[400px] min-w-[300px] lg:flex lg:flex-col">{sidebarContent}</div>

      <div
        className={collapsibleDrawerUnderlayClasses({ isOpen: isMobileOpen })}
        onClick={() => setIsMobileOpen(false)}
      />

      <div
        className={collapsibleDrawerClasses({ isOpen: isMobileOpen })}
        style={{ left: isMobileOpen ? 0 : -100 }}
      >
        <button
          type="button"
          onClick={() => setIsMobileOpen(false)}
          className="fixed right-4 top-4 z-[100] rounded-lg border border-gray-300 bg-white p-2"
          aria-label="Close menu"
        >
          <Icon icon={SpriteMap.CloseIcon} className="size-3 text-green-500" />
        </button>

        <div className="flex-1 overflow-y-auto pt-8">{sidebarContent}</div>
      </div>
    </div>
  )
}
